import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  CredentialsUpdateConfigRequest,
  CredentialsUpsertRequest,
  Oauth2SlackAuthRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { requireWorkspaceId } from '../shared/workspace';
import type { HookMutationConfig } from '../shared/react-query-types';
import { environmentKeys } from '../environment/entity';
import { actionCredentialKeys, type ActionCredentialType } from './queries';

export type ActionCredentialWriteInput = CredentialsUpsertRequest;

export interface UpsertActionCredentialInput extends ActionCredentialWriteInput {
  workspaceId: number;
  actionType: ActionCredentialType;
}

/**
 * Creates or replaces a credential slot. Feature-toggle types (url_params,
 * time_zone_match, …) enable by upserting empty fields — the row's presence is
 * the enabled signal.
 *
 * @public
 */
export async function upsertActionCredential({
  workspaceId,
  actionType,
  ...write
}: UpsertActionCredentialInput): Promise<void> {
  await hydraUiApi.credentials.update({
    params: { path: { environment_id: String(workspaceId), action_type: actionType } },
    body: write,
  });
}

export interface DeleteActionCredentialInput {
  workspaceId: number;
  actionType: ActionCredentialType;
}

/** @public */
export async function deleteActionCredential({ workspaceId, actionType }: DeleteActionCredentialInput): Promise<void> {
  await hydraUiApi.credentials.delete({
    params: { path: { environment_id: String(workspaceId), action_type: actionType } },
  });
}

export interface UpdateActionCredentialConfigInput extends CredentialsUpdateConfigRequest {
  workspaceId: number;
  actionType: ActionCredentialType;
}

/**
 * Rewrites only a credential's `config` string, leaving its username and
 * password untouched. The push channel uses it for the settings that live in
 * that string — the APNS sandbox switch, and whether the Android FCM key also
 * serves iOS — so flipping one doesn't require re-uploading the key file.
 *
 * @public
 */
export async function updateActionCredentialConfig({
  workspaceId,
  actionType,
  ...write
}: UpdateActionCredentialConfigInput): Promise<void> {
  await hydraUiApi.credentials.config({
    params: { path: { environment_id: String(workspaceId), action_type: actionType } },
    body: write,
  });
}

/**
 * The credential slots whose `config` string the workspace environment reads
 * back: `push_providers` is derived from them, so it is stale the moment one of
 * these is written.
 */
const PUSH_ACTION_TYPES: readonly ActionCredentialType[] = ['push_apns', 'push_fcm'];

/**
 * Reload everything a write to one credential slot can change: the slot itself,
 * plus — for the push slots — the workspace environment, whose `push_providers`
 * is derived from those two credentials and therefore decides which platforms
 * the push settings show as configured.
 */
async function invalidateAfterCredentialWrite(
  qc: QueryClient,
  workspaceId: number,
  actionType: ActionCredentialType,
): Promise<void> {
  const invalidations = [qc.invalidateQueries({ queryKey: actionCredentialKeys.detail(workspaceId, actionType) })];
  if (PUSH_ACTION_TYPES.includes(actionType)) {
    invalidations.push(qc.invalidateQueries({ queryKey: environmentKeys.current(workspaceId) }));
  }
  await Promise.all(invalidations);
}

interface UseActionCredentialMutationOptions<TVariables extends { actionType: ActionCredentialType }> {
  workspaceId?: number;
  mutationConfig?: HookMutationConfig<void, TVariables>;
}

/**
 * Shared hook body for the credential writes: resolve the workspace, run the
 * write, then invalidate that credential's slot so observers reload it — the
 * write's response isn't the read's shape, so the fresh state comes from the
 * refetch. The invalidation is awaited, so `mutateAsync` settles only once the
 * slot has reloaded: a caller holding a prompt open through the write keeps it
 * open until the page shows the new state.
 */
function useActionCredentialMutation<TVariables extends { actionType: ActionCredentialType }>(
  runMutation: (workspaceId: number, variables: TVariables) => Promise<void>,
  { workspaceId: overrideWorkspaceId, mutationConfig }: UseActionCredentialMutationOptions<TVariables> = {},
) {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: TVariables) => runMutation(workspaceId, variables),
    onSuccess: async (result, variables, ...callbackArgs) => {
      await invalidateAfterCredentialWrite(qc, workspaceId, variables.actionType);
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
}

type UseUpsertActionCredentialVariables = Omit<UpsertActionCredentialInput, 'workspaceId'>;

/** @public */
export const useUpsertActionCredential = (
  options: UseActionCredentialMutationOptions<UseUpsertActionCredentialVariables> = {},
) =>
  useActionCredentialMutation<UseUpsertActionCredentialVariables>(
    (workspaceId, variables) => upsertActionCredential({ workspaceId, ...variables }),
    options,
  );

type UseUpdateActionCredentialConfigVariables = Omit<UpdateActionCredentialConfigInput, 'workspaceId'>;

/** @public */
export const useUpdateActionCredentialConfig = (
  options: UseActionCredentialMutationOptions<UseUpdateActionCredentialConfigVariables> = {},
) =>
  useActionCredentialMutation<UseUpdateActionCredentialConfigVariables>(
    (workspaceId, variables) => updateActionCredentialConfig({ workspaceId, ...variables }),
    options,
  );

type UseDeleteActionCredentialVariables = Omit<DeleteActionCredentialInput, 'workspaceId'>;

/** @public */
export const useDeleteActionCredential = (
  options: UseActionCredentialMutationOptions<UseDeleteActionCredentialVariables> = {},
) =>
  useActionCredentialMutation<UseDeleteActionCredentialVariables>(
    (workspaceId, variables) => deleteActionCredential({ workspaceId, ...variables }),
    options,
  );

export type SlackConnectInput = Oauth2SlackAuthRequest;

export interface ConnectSlackActionCredentialInput extends SlackConnectInput {
  workspaceId: number;
}

/**
 * Exchanges the authorization code the Slack OAuth popup returned for a bot
 * token and stores it as the workspace's `slack` credential. The backend does
 * the exchange, so the popup's `redirect_uri` travels along for Slack to match
 * against the one the code was issued for.
 *
 * @public
 */
export async function connectSlackActionCredential({
  workspaceId,
  ...body
}: ConnectSlackActionCredentialInput): Promise<void> {
  await hydraUiApi.oauth2.slack({
    params: { path: { environment_id: String(workspaceId) } },
    body,
  });
}

/**
 * The connect writes the `slack` slot, so it invalidates that slot like the
 * other credential writes; the variables carry no `actionType` because the
 * endpoint is Slack-only.
 *
 * @public
 */
export const useConnectSlackActionCredential = ({
  workspaceId: overrideWorkspaceId,
  mutationConfig,
}: { workspaceId?: number; mutationConfig?: HookMutationConfig<void, SlackConnectInput> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (variables: SlackConnectInput) => connectSlackActionCredential({ workspaceId, ...variables }),
    onSuccess: async (result, variables, ...callbackArgs) => {
      await qc.invalidateQueries({ queryKey: actionCredentialKeys.detail(workspaceId, 'slack') });
      onSuccess?.(result, variables, ...callbackArgs);
    },
  });
};
